// src/components/admin/AdminDashboard.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './AdminDashboard.css';

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    total_leads: 0, meetings_booked: 0, ready_for_onboarding: 0,
    payment_pending: 0, payment_failed: 0, content_pending: 0,
    stores_in_setup: 0, ready_for_review: 0, stores_live: 0,
    open_tickets: 0, disabled_stores: 0,
  });
  const [attention, setAttention] = useState({
    leads_ready_onboarding: [], payment_pending: [], payment_failed: [],
    ready_for_review: [], content_pending: [], open_tickets: [],
  });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    checkAuth();
    fetchStats();
    fetchAttention();
  }, []);

  const checkAuth = () => {
    const token = localStorage.getItem('adminToken');
    if (!token) navigate('/admin/login');
  };

  const fetchStats = async () => {
    try {
      const res = await fetch('https://creativepulse.pk/main-api/get-dashboard-stats.php');
      const data = await res.json();
      if (data.success) setStats(data.stats);
    } catch (e) {
      console.error('Error fetching dashboard stats:', e);
    }
  };

  const fetchAttention = async () => {
    try {
      const res = await fetch('https://creativepulse.pk/main-api/get-dashboard-attention.php');
      const data = await res.json();
      if (data.success) setAttention(data);
    } catch (e) {
      console.error('Error fetching attention data:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminData');
    navigate('/admin/login');
  };

  const statCards = [
    { title: 'Total Leads', value: stats.total_leads, color: '#1d4ed8', bg: '#dbeafe' },
    { title: 'Meetings Booked', value: stats.meetings_booked, color: '#7c3aed', bg: '#ede9fe' },
    { title: 'Ready for Onboarding', value: stats.ready_for_onboarding, color: '#1e8a4c', bg: '#dcf5e3' },
    { title: 'Payment Pending', value: stats.payment_pending, color: '#c2560a', bg: '#fde3d0' },
    // TODO: no submission flow exists yet — real query, genuinely 0 for now
    { title: 'Content Pending', value: stats.content_pending, color: '#a3760a', bg: '#fdf1d6' },
    { title: 'Stores in Setup', value: stats.stores_in_setup, color: '#4338ca', bg: '#eef2ff' },
    { title: 'Ready for Review', value: stats.ready_for_review, color: '#7c3aed', bg: '#ede9fe' },
    { title: 'Stores Live', value: stats.stores_live, color: '#1e8a4c', bg: '#dcf5e3' },
    { title: 'Open Tickets', value: stats.open_tickets, color: '#c2560a', bg: '#fde3d0' },
    { title: 'Payment Failed', value: stats.payment_failed, color: '#c8342a', bg: '#fbdede' },
    // TODO: real query, but no disable-toggle UI exists yet — always 0 until built
    { title: 'Disabled Stores', value: stats.disabled_stores, color: '#8a8a8a', bg: '#f0f0f0' },
  ];

  const attentionPanels = [
    {
      title: 'Leads Ready for Onboarding',
      items: attention.leads_ready_onboarding,
      renderLabel: (i) => `${i.lead_name}${i.business_name ? ` — ${i.business_name}` : ''}`,
      onViewAll: () => navigate('/admin/leads'),
      empty: 'No leads waiting on onboarding.',
    },
    {
      title: 'Payment Pending',
      items: attention.payment_pending,
      renderLabel: (i) => `${i.lead_name}${i.business_name ? ` — ${i.business_name}` : ''}`,
      onViewAll: () => navigate('/admin/leads'),
      empty: 'No pending payments.',
    },
    {
      title: 'Content Pending',
      items: attention.content_pending,
      renderLabel: (i) => `${i.store_admin_email || 'Unknown store'} — ${i.content_type || 'content'}`,
      onViewAll: () => navigate('/admin/leads'),
      empty: 'Nothing submitted yet — no intake flow built for this.',
      isPlaceholderFeature: true,
    },
    {
      title: 'Ready for Review',
      items: attention.ready_for_review,
      renderLabel: (i) => `${i.lead_name}${i.business_name ? ` — ${i.business_name}` : ''}`,
      onViewAll: () => navigate('/admin/leads'),
      empty: 'Nothing ready for review.',
    },
    {
      title: 'Open Support Tickets',
      items: attention.open_tickets,
      renderLabel: (i) => `${i.ticket_number} — ${i.subject}`,
      onViewAll: () => navigate('/admin/support-tickets'),
      empty: 'No open tickets.',
    },
    {
      title: 'Payment Failed / Grace Period',
      items: attention.payment_failed,
      renderLabel: (i) => `${i.lead_name}${i.business_name ? ` — ${i.business_name}` : ''}`,
      onViewAll: () => navigate('/admin/leads'),
      empty: 'No failed payments.',
    },
  ];

  return (
    <div className="admin-dashboard">
      <div className="dashboard-header">
        <div>
          <h1>Internal Dashboard</h1>
          <p className="dashboard-subtitle">The central command center for leads, payments, content, setup, launches, and support.</p>
        </div>
        <button onClick={handleLogout} className="logout-btn">Logout</button>
      </div>

      <div className="stats-grid">
        {statCards.map((stat, index) => (
          <div key={index} className="stat-card">
            <p className="stat-title">{stat.title}</p>
            <h3 className="stat-value" style={{ color: stat.color }}>{loading ? '—' : stat.value}</h3>
            <span className="stat-sub-badge" style={{ background: stat.bg, color: stat.color }}>Live</span>
          </div>
        ))}
      </div>

      <div className="attention-section">
        <h2 className="attention-title">Attention Required</h2>
        <div className="attention-grid">
          {attentionPanels.map((panel, idx) => (
            <div key={idx} className={`attention-panel ${panel.isPlaceholderFeature ? 'attention-panel-soon' : ''}`}>
              <div className="attention-panel-header">
                <h3>{panel.title}</h3>
                <button onClick={panel.onViewAll} className="attention-viewall-link">View all →</button>
              </div>
              {loading ? (
                <p className="attention-empty">Loading...</p>
              ) : panel.items.length === 0 ? (
                <p className="attention-empty">{panel.empty}</p>
              ) : (
                <ul className="attention-list">
                  {panel.items.map((item) => (
                    <li key={item.id}>
                      <span>{panel.renderLabel(item)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;